import React, { useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Alert,
  Platform,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import axios from "axios";
import moment from "moment-timezone";
import { Feather, MaterialCommunityIcons } from "@expo/vector-icons";
import { useNavigation } from "@react-navigation/native";

let ExcelJS, saveAs;
if (Platform.OS === "web") {
  ExcelJS = require("exceljs");
  saveAs = require("file-saver").saveAs;
}

const BackButton = ({ color }) => {
  const navigation = useNavigation();

  return (
    <TouchableOpacity
      style={styles.backButton}
      onPress={() => navigation.goBack()}
      accessibilityLabel="Go back"
    >
      <Feather name="arrow-left" size={24} color={color} />
    </TouchableOpacity>
  );
};

const formatNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue.toFixed(2) : "";
};

const parseRate = (value) => {
  if (value === null || value === undefined || value === "") {
    return Number.NaN;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : Number.NaN;
};

const getCurrentPayPeriod = () => {
  const now = moment.tz("America/Phoenix");
  const thisSunday = now.clone().startOf("week");
  const tuesdayNoon = thisSunday
    .clone()
    .add(2, "days")
    .hour(12)
    .minute(0)
    .second(0);

  if (now.isBefore(tuesdayNoon)) {
    return thisSunday.clone().subtract(1, "week");
  }

  return thisSunday;
};

const PayrollWorkbookExport = () => {
  const navigation = useNavigation();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [attendanceData, setAttendanceData] = useState([]);
  const [employeeDirectory, setEmployeeDirectory] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const payPeriodStart = useMemo(() => getCurrentPayPeriod(), []);
  const payPeriodEnd = useMemo(
    () => payPeriodStart.clone().endOf("week"),
    [payPeriodStart]
  );

  const fetchPayrollData = async () => {
    setLoading(true);
    try {
      const [attendanceResponse, employeesResponse] = await Promise.all([
        axios.get(
          `https://sbg-backend.onrender.com/attendances?currentPeriod=${payPeriodStart.format(
            "YYYY-MM-DD"
          )}`
        ),
        axios.get("https://sbg-backend.onrender.com/employees?includeInactive=true"),
      ]);

      setAttendanceData(attendanceResponse.data?.attendances || []);
      setEmployeeDirectory(Array.isArray(employeesResponse.data) ? employeesResponse.data : []);
    } catch (error) {
      console.error("Error loading payroll export data:", error);
      Alert.alert("Error", "Failed to load payroll export data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayrollData();
  }, []);

  const getEmployeeMeta = (attendance) => {
    return (
      employeeDirectory.find(
        (employee) => employee.employeeId === attendance.employeeId
      ) || {}
    );
  };

  const getLeaveHours = (attendance) => {
    if (!Array.isArray(attendance.days)) {
      return 0;
    }

    return attendance.days.reduce((total, day) => {
      if (!Array.isArray(day.hours)) {
        return total;
      }

      return (
        total +
        day.hours.reduce((dayTotal, hour) => {
          if (!hour || hour.type === "work") {
            return dayTotal;
          }

          return dayTotal + (Number(hour.amount) || 0);
        }, 0)
      );
    }, 0);
  };

  const getRegularHours = (attendance) => {
    const weeklyHours = Number(attendance.weeklyTotalHours) || 0;
    return {
      straightTime: Math.min(weeklyHours, 40),
      overtime: Math.max(weeklyHours - 40, 0),
    };
  };

  const getSupervisorName = (employee) => {
    if (employee?.supervisorId) {
      return employee.supervisorId;
    }

    const firstSupervisor = Array.isArray(employee?.supervisors)
      ? employee.supervisors[0]
      : null;

    return firstSupervisor?.id || firstSupervisor?.email || "";
  };

  const getBackupSupervisorName = (employee) => {
    const backupSupervisor = Array.isArray(employee?.supervisors)
      ? employee.supervisors[1]
      : null;

    return backupSupervisor?.id || backupSupervisor?.email || "";
  };

  const getGrossPay = (employee, straightTime, overtime, leaveHours) => {
    const payRate = parseRate(
      employee?.payRate ?? employee?.hourlyRate ?? employee?.straightPayRate
    );

    if (!Number.isFinite(payRate)) {
      return "";
    }

    const overtimeRate = parseRate(
      employee?.otPayRate ??
        employee?.overtimePayRate ??
        payRate * 1.5
    );

    return formatNumber(
      straightTime * payRate +
        overtime * (Number.isFinite(overtimeRate) ? overtimeRate : payRate * 1.5) +
        leaveHours * payRate
    );
  };

  const getInvoiceAmount = (employee, straightTime, overtime) => {
    const stBillRate = parseRate(employee?.stBillRate ?? employee?.straightBillRate);
    const otBillRate = parseRate(employee?.otBillRate ?? employee?.overtimeBillRate);

    if (!Number.isFinite(stBillRate) && !Number.isFinite(otBillRate)) {
      return "";
    }

    const safeStBillRate = Number.isFinite(stBillRate) ? stBillRate : 0;
    const safeOtBillRate = Number.isFinite(otBillRate) ? otBillRate : safeStBillRate;

    return formatNumber(straightTime * safeStBillRate + overtime * safeOtBillRate);
  };

  const filteredAttendanceData = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return attendanceData;
    }

    return attendanceData.filter((attendance) => {
      return (
        attendance.employeeName?.toLowerCase().includes(query) ||
        attendance.employeeId?.toLowerCase().includes(query) ||
        moment
          .tz(attendance.weekStart, "America/Phoenix")
          .format("YYYY-MM-DD")
          .includes(query)
      );
    });
  }, [attendanceData, searchQuery]);

  const generateWorkbook = async () => {
    if (Platform.OS !== "web") {
      Alert.alert("Export to Excel is only available on web.");
      return;
    }

    if (!ExcelJS || !saveAs) {
      Alert.alert("Error", "Excel export dependencies are unavailable.");
      return;
    }

    try {
      setExporting(true);

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Payroll Workbook");

      worksheet.columns = [
        { header: "Hire Date", key: "hireDate", width: 14 },
        { header: "Employee", key: "employee", width: 24 },
        { header: "Job Title", key: "jobTitle", width: 24 },
        { header: "Department", key: "department", width: 22 },
        { header: "Payroll notes", key: "payrollNotes", width: 28 },
        { header: "PAY RATE ", key: "payRate", width: 12 },
        { header: "OT Pay Rate", key: "otPayRate", width: 12 },
        { header: "Week Period", key: "weekPeriod", width: 22 },
        { header: "Straight Time ", key: "straightTime", width: 14 },
        { header: "A =Approved", key: "straightApproved", width: 12 },
        { header: "Over Time", key: "overtime", width: 12 },
        { header: "A =Approved", key: "overtimeApproved", width: 12 },
        { header: "Pay Leave Request", key: "leaveHours", width: 16 },
        { header: "A =Approved", key: "leaveApproved", width: 12 },
        { header: "Total Billable Hours", key: "billableHours", width: 18 },
        { header: "", key: "spacer1", width: 4 },
        { header: "Gross pay", key: "grossPay", width: 14 },
        { header: "", key: "spacer2", width: 4 },
        { header: "ST Bill Rate ", key: "stBillRate", width: 14 },
        { header: " OT Bill Rate", key: "otBillRate", width: 14 },
        { header: "", key: "spacer3", width: 4 },
        { header: "Total Invoice", key: "totalInvoice", width: 14 },
        { header: "SC# & PO# NOTES", key: "notes", width: 24 },
        { header: "INVOICE STATUS", key: "invoiceStatus", width: 16 },
        { header: "SUPERVISOR", key: "supervisor", width: 20 },
        { header: "", key: "spacer4", width: 4 },
        { header: "Backup SUPERVISOR", key: "backupSupervisor", width: 20 },
      ];

      worksheet.getRow(1).font = { bold: true };
      worksheet.getRow(1).alignment = {
        vertical: "middle",
        horizontal: "center",
        wrapText: true,
      };
      worksheet.getRow(1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFF59D0A" },
      };
      worksheet.getRow(1).border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
      worksheet.views = [{ state: "frozen", ySplit: 1 }];
      worksheet.autoFilter = "A1:AA1";

      filteredAttendanceData.forEach((attendance) => {
        const employee = getEmployeeMeta(attendance);
        const weeklyHours = Number(attendance.weeklyTotalHours) || 0;
        const { straightTime, overtime } = getRegularHours(attendance);
        const leaveHours = attendance.isSpecialFormat ? 0 : getLeaveHours(attendance);
        const weekStart = moment
          .tz(attendance.weekStart, "America/Phoenix")
          .startOf("week");
        const weekEnd = weekStart.clone().endOf("week");
        const payRate = parseRate(
          employee?.payRate ?? employee?.hourlyRate ?? employee?.straightPayRate
        );
        const otPayRate = parseRate(
          employee?.otPayRate ??
            employee?.overtimePayRate ??
            (Number.isFinite(payRate) ? payRate * 1.5 : Number.NaN)
        );
        const stBillRate = parseRate(
          employee?.stBillRate ?? employee?.straightBillRate
        );
        const otBillRate = parseRate(
          employee?.otBillRate ?? employee?.overtimeBillRate
        );
        const grossPay = getGrossPay(employee, straightTime, overtime, leaveHours);
        const totalInvoice = getInvoiceAmount(employee, straightTime, overtime);

        worksheet.addRow({
          hireDate: employee?.hireDate
            ? moment(employee.hireDate).tz("America/Phoenix").format("YYYY-MM-DD")
            : "",
          employee: attendance.employeeName || employee?.employeeName || "",
          jobTitle: attendance.jobTitle || employee?.jobTitle || "",
          department: attendance.department || employee?.department || "",
          payrollNotes:
            Array.isArray(attendance.notes) && attendance.notes.length > 0
              ? attendance.notes.map((note) => note.content).join("; ")
              : "",
          payRate: Number.isFinite(payRate) ? formatNumber(payRate) : "",
          otPayRate: Number.isFinite(otPayRate) ? formatNumber(otPayRate) : "",
          weekPeriod: `${weekStart.format("YYYY-MM-DD")} - ${weekEnd.format(
            "YYYY-MM-DD"
          )}`,
          straightTime: formatNumber(straightTime),
          straightApproved: attendance.approved ? "A" : "",
          overtime: formatNumber(overtime),
          overtimeApproved: attendance.approved ? "A" : "",
          leaveHours: formatNumber(leaveHours),
          leaveApproved: attendance.approved ? "A" : "",
          billableHours: formatNumber(weeklyHours + leaveHours),
          grossPay,
          stBillRate: Number.isFinite(stBillRate) ? formatNumber(stBillRate) : "",
          otBillRate: Number.isFinite(otBillRate) ? formatNumber(otBillRate) : "",
          totalInvoice,
          notes:
            Array.isArray(attendance.notes) && attendance.notes.length > 0
              ? attendance.notes
                  .map((note) => `${note.content} (${note.supervisorName})`)
                  .join("; ")
              : "",
          invoiceStatus: attendance.approved ? "A" : "",
          supervisor: getSupervisorName(employee),
          backupSupervisor: getBackupSupervisorName(employee),
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, "WeeklyPayrollWorkbook.xlsx");
    } catch (error) {
      console.error("Error generating payroll workbook:", error);
      Alert.alert("Error", "Failed to generate the payroll workbook.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <BackButton color="#222" />
        <View style={styles.titleWrap}>
          <MaterialCommunityIcons name="file-excel" size={22} color="#1B8A3D" />
          <Text style={styles.title}>Payroll Workbook</Text>
        </View>
        <TouchableOpacity onPress={fetchPayrollData} style={styles.refreshButton}>
          <Feather name="refresh-cw" size={18} color="#1f6feb" />
        </TouchableOpacity>
      </View>

      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.card}>
          <Text style={styles.cardTitle}>Current Pay Period</Text>
          <Text style={styles.cardText}>
            {payPeriodStart.format("MMM D, YYYY")} - {payPeriodEnd.format("MMM D, YYYY")}
          </Text>
          <Text style={styles.cardText}>
            Records loaded: {attendanceData.length}
          </Text>
          <Text style={styles.cardNote}>
            This export pulls employee info, weekly hours, and any available pay-rate fields into the workbook template.
          </Text>
        </View>

        <View style={styles.controlsRow}>
          <TextInput
            style={styles.searchInput}
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholder="Search by employee name, ID, or week"
            placeholderTextColor="#8a8a8a"
          />
          <TouchableOpacity
            style={styles.exportButton}
            onPress={generateWorkbook}
            disabled={exporting || loading}
          >
            {exporting ? (
              <ActivityIndicator color="#ffffff" />
            ) : (
              <Text style={styles.exportButtonText}>Export Excel</Text>
            )}
          </TouchableOpacity>
        </View>

        {loading ? (
          <View style={styles.loadingWrap}>
            <ActivityIndicator size="large" color="#1f6feb" />
            <Text style={styles.loadingText}>Loading payroll data...</Text>
          </View>
        ) : (
          <View style={styles.listWrap}>
            {filteredAttendanceData.map((attendance) => {
              const employee = getEmployeeMeta(attendance);
              const { straightTime, overtime } = getRegularHours(attendance);
              const leaveHours = attendance.isSpecialFormat ? 0 : getLeaveHours(attendance);

              return (
                <View key={attendance._id} style={styles.rowCard}>
                  <Text style={styles.rowTitle}>
                    {attendance.employeeName || employee?.employeeName || "Unknown Employee"}
                  </Text>
                  <Text style={styles.rowText}>
                    ID: {attendance.employeeId}
                  </Text>
                  <Text style={styles.rowText}>
                    Week: {moment.tz(attendance.weekStart, "America/Phoenix").format("MMM D, YYYY")}
                  </Text>
                  <Text style={styles.rowText}>
                    Hours: {formatNumber(straightTime)} straight, {formatNumber(overtime)} overtime, {formatNumber(leaveHours)} leave
                  </Text>
                </View>
              );
            })}

            {filteredAttendanceData.length === 0 && (
              <View style={styles.emptyState}>
                <Text style={styles.emptyTitle}>No records found</Text>
                <Text style={styles.emptyText}>
                  Try a different search or refresh to load the current pay period.
                </Text>
              </View>
            )}
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#f5f7fb",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: "#ffffff",
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
  },
  titleWrap: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  title: {
    fontSize: 18,
    fontWeight: "700",
    color: "#1f2937",
  },
  refreshButton: {
    padding: 8,
  },
  content: {
    padding: 16,
    paddingBottom: 28,
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    shadowColor: "#000",
    shadowOpacity: 0.08,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 4 },
    elevation: 2,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: "700",
    color: "#111827",
    marginBottom: 6,
  },
  cardText: {
    fontSize: 14,
    color: "#374151",
    marginBottom: 4,
  },
  cardNote: {
    marginTop: 10,
    fontSize: 13,
    color: "#6b7280",
    lineHeight: 18,
  },
  controlsRow: {
    gap: 12,
    marginBottom: 16,
  },
  searchInput: {
    backgroundColor: "#ffffff",
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 14,
    color: "#111827",
  },
  exportButton: {
    backgroundColor: "#1B8A3D",
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 14,
  },
  exportButtonText: {
    color: "#ffffff",
    fontSize: 15,
    fontWeight: "700",
  },
  loadingWrap: {
    paddingVertical: 32,
    alignItems: "center",
    justifyContent: "center",
  },
  loadingText: {
    marginTop: 10,
    fontSize: 14,
    color: "#6b7280",
  },
  listWrap: {
    gap: 12,
  },
  rowCard: {
    backgroundColor: "#ffffff",
    borderRadius: 14,
    padding: 14,
    borderLeftWidth: 4,
    borderLeftColor: "#1B8A3D",
    shadowColor: "#000",
    shadowOpacity: 0.06,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 3 },
    elevation: 1,
  },
  rowTitle: {
    fontSize: 16,
    fontWeight: "700",
    color: "#111827",
    marginBottom: 4,
  },
  rowText: {
    fontSize: 13,
    color: "#4b5563",
    marginTop: 2,
  },
  emptyState: {
    paddingVertical: 28,
    alignItems: "center",
    justifyContent: "center",
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: "700",
    color: "#111827",
    marginBottom: 6,
  },
  emptyText: {
    fontSize: 13,
    color: "#6b7280",
    textAlign: "center",
    maxWidth: 360,
  },
  backButton: {
    padding: 8,
  },
});

export default PayrollWorkbookExport;
